import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** The light-blue uppercase pill the Smart Form design puts beside the form title. */
function HeaderPill({
  children,
  testId,
  uppercase = true,
  title,
}: {
  children: ReactNode;
  testId?: string;
  uppercase?: boolean;
  title?: string;
}) {
  return (
    <span
      data-testid={testId}
      title={title}
      style={{
        display: 'inline-block',
        // The pill is a header strip's widest unwrappable word, so an uncapped one sets the page's
        // minimum width: a 150-character name pushed a 1,440px window out to 2,029px (BMO #350).
        // Cap it and let the text ellipsize — the full value stays on the pill's hover title.
        maxWidth: 'min(32ch, 100%)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        padding: '4px 12px',
        borderRadius: 4,
        background: '#cfe6f7',
        color: '#0f172a',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: uppercase ? 'uppercase' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * "CLIENT: JACK" — the primary account holder's name beside the form title, per the Smart Form
 * design (RDSP GAMXDPT-275). Renders nothing until there is a name to show.
 */
export function ClientBadge({ name }: { name: string | null }) {
  const { t } = useTranslation();
  if (!name) return null;
  // The client's name as it was typed — "Rita Testinvestor", not "CLIENT: RITA TESTINVESTOR"
  // (QA RIF TC-05-01, user decision 2026-10-02). "Client:" stays as the pill's hover text.
  return (
    <HeaderPill
      testId="client-badge"
      uppercase={false}
      title={t('formRenderer.clientBadge', { name })}
    >
      {name}
    </HeaderPill>
  );
}

/**
 * "APP ID #IL27F87874" — BMO's application id beside the form title, per the Smart Form design.
 * Renders nothing until the first save has minted an id.
 */
export function AppIdBadge({ id }: { id: string | null | undefined }) {
  const { t } = useTranslation();
  if (!id) return null;
  const label = t('advisor.application.appIdBadge', { id });
  // `title` matches the pill's own text: ids are short enough never to reach the width cap today,
  // but an id that did would otherwise ellipsize with nothing to hover for the rest.
  return (
    <HeaderPill testId="app-id-badge" title={label}>
      {label}
    </HeaderPill>
  );
}
